import React from "react";
import { useIntl } from "react-intl";
import { Space, Row } from "@masterteam/mt-design-react";
import { useSelector } from "react-redux";

import { object, date, number, array, string } from "yup";

import KPIMeasuresField from "../../KPIMeasuresField";
import CustomKPIMeasures from "../../CustomKPIMeasures";

import { WizardStep, Field } from "../Wizard";

import { UnitOfMeasure, cleanKPIMeasures } from "../../../helpers";

export const controlsValidationSchema = (hasNoTargetBaseline) => {
  return object().shape({
    Baseline: hasNoTargetBaseline
      ? number()
          .nullable()
          .when("Polarity", {
            is: (currentPolarity) => currentPolarity.Value === "Positive",
            then: (schema) => schema.min(0, " "),
            otherwise: (schema) => schema.min(0, " "),
          })
      : number()
          .required(" ")
          .when("Polarity", {
            is: (currentPolarity) => currentPolarity.Value === "Positive",
            then: (schema) => schema.min(0, " "),
            otherwise: (schema) => schema.min(0, " "),
          }),
    Years: number().required(" ").min(1, " ").max(5, " "),
    CustomFrequency: string()
      .nullable()
      .when("Frequency", {
        is: (frequency) => frequency.Value === "custom",
        then: (schema) => schema.required(" "),
      }),
    DataSource: object().required(" ").nullable(),
    Frequency: object().required(" ").nullable(),
    StartDate: date().required(" "),
    BaselineDate: date().required(" "),
    KPIMeasures: array()
      .min(1, " ")
      .when(
        ["Polarity", "CalculationMethod"],
        (polarity, calculationMethod, schema) => {
          const shape = {};

          // shape.Target = hasNoTargetBaseline
          //   ? number()
          //       .min(polarity.Value === "Positive" ? 0 : 0, " ")
          //       .nullable()
          //   : number()
          //       .required(" ")
          //       .min(polarity.Value === "Positive" ? 0 : 0, " ");

          shape.DueDate = date(" ").required(" ");

          shape.Target = number().when("IsEditable", {
            is: true,
            then: hasNoTargetBaseline
              ? number()
                  .min(polarity.Value === "Positive" ? 0 : 0, " ")
                  .nullable()
              : number()
                  .required(" ")
                  .min(polarity.Value === "Positive" ? 0 : 0, " "),
            otherwise: number()
              .nullable()
              .min(polarity.Value === "Positive" ? 0 : 0, " "),
          });

          if (calculationMethod.Value === "4") {
            shape.MaxTarget = number()
              .required(" ")
              .when(["Target"], (target, sch) => {
                if (target) {
                  return sch.min(target, " ");
                } else {
                  return sch.min(polarity.Value === "Positive" ? 0 : 0, " ");
                }
              });
          }
          return schema.of(object().shape(shape));
        }
      ),
  });
};

function Controllers({
  formBag,
  setHasParameter,
  isEditMode,
  yearsValue,
  isReadOnly,
}) {
  const intl = useIntl();
  const isAr = intl.locale === "ar";

  const [kpiSources, frequencies] = useSelector(({ theme }) => [
    theme.KPISource.item.Items.filter((_item) => _item.Value !== "auto"),
    [...theme.Frequencies.item.Items],
  ]);

  const { values, setFieldValue } = formBag;

  return (
    <WizardStep name="KPI Controls">
      <Space direction="vertical" size="medium" className="details-container">
        <Row size="small">
          <Field
            type="select"
            name="DataSource"
            disabled={
              isEditMode ||
              isReadOnly ||
              values?.HasNoTarget ||
              values?.HasNoBaseline
            }
            label={intl.formatMessage({ id: "kpi.data-source" })}
            inputProps={{
              options: kpiSources,
              getOptionLabel: (el) => (isAr ? el.Arabic : el.English),
              getOptionValue: (el) => el.Value,
              placeholder: intl.formatMessage({ id: "kpi.data-source" }),
              onChange: (value) => {
                setFieldValue("DataSource", value);
                setHasParameter(value?.Value === "semi-auto");
              },
            }}
            hideErrorMessage
            required
          />
          <Field
            type="date"
            name="StartDate"
            disabled={isEditMode || isReadOnly}
            label={intl.formatMessage({ id: "kpi.start-date" })}
            inputProps={{
              placeholder: intl.formatMessage({ id: "kpi.start-date" }),
              onChange: (e) => {
                setFieldValue("StartDate", e);
                setFieldValue(
                  "KPIMeasures",
                  cleanKPIMeasures(values.Years, e, values.Frequency)
                );
              },
            }}
            hideErrorMessage
            required
          />
        </Row>
        <Row size="small">
          <Field
            name="Baseline"
            disabled={
              isReadOnly || values?.HasNoTarget || values?.HasNoBaseline
            }
            id="baseline"
            type="number"
            label={intl.formatMessage({ id: "kpi.baseline" })}
            inputProps={{
              min: 0,
              step: "any",
              onKeyDown: (evt) => {
                if (evt.key === "e" || evt.key === "-" || evt.key === "+") {
                  evt.preventDefault();
                }
              },
              onChange: (event) => {
                setFieldValue("Baseline", event.target.value);
              },
              placeholder: `${intl.formatMessage({
                id: "kpi.baseline",
              })} ${UnitOfMeasure(
                values.UnitOfMeasure && values.UnitOfMeasure?.value
              )}`,
            }}
            hideErrorMessage
            required
          />
          <Field
            type="date"
            label={intl.formatMessage({ id: "common.baseline-date" })}
            name="BaselineDate"
            disabled={
              isReadOnly || values?.HasNoTarget || values?.HasNoBaseline
            }
            hideErrorMessage
            required
          />
        </Row>
        <Row size="small">
          <Field
            type="select"
            name="Frequency"
            disabled={isEditMode || isReadOnly}
            label={intl.formatMessage({ id: "kpi-info.kpi-frequency" })}
            inputProps={{
              options: frequencies,
              getOptionLabel: (el) => (isAr ? el.Arabic : el.English),
              getOptionValue: (el) => el.Value,
              placeholder: intl.formatMessage({ id: "kpi-info.kpi-frequency" }),
              onChange: (e) => {
                setFieldValue("Frequency", e);
                setFieldValue("CustomFrequency", "");
                setFieldValue(
                  "KPIMeasures",
                  e.Value === "custom"
                    ? cleanKPIMeasures(values.Years, values.StartDate, e)
                    : []
                );
              },
            }}
            hideErrorMessage
            required
          />

          {values?.Frequency?.Value === "custom" ? (
            <Field
              name="CustomFrequency"
              label={intl.formatMessage({ id: "kpi.custom-frequency" })}
              inputProps={{
                placeholder: intl.formatMessage({ id: "kpi.custom-frequency" }),
              }}
              disabled={isEditMode || isReadOnly}
              hideErrorMessage
              required
            />
          ) : (
            <Field
              type="number"
              name="Years"
              label={intl.formatMessage({ id: "kpi.number-of-years" })}
              inputProps={{
                placeholder: intl.formatMessage({ id: "kpi.number-of-years" }),
                min: yearsValue || 1,
                max: "5",
                onChange: (e) => {
                  const { value } = e.target;

                  if (!value || value < yearsValue) {
                    setFieldValue("Years", yearsValue);
                    setFieldValue(
                      "KPIMeasures",
                      cleanKPIMeasures(
                        yearsValue,
                        values.StartDate,
                        values.Frequency,
                        values.KPIMeasures
                      )
                    );
                    return;
                  }

                  setFieldValue("Years", value);
                  setFieldValue(
                    "KPIMeasures",
                    cleanKPIMeasures(
                      value,
                      values.StartDate,
                      values.Frequency,
                      values.KPIMeasures
                    )
                  );
                },
              }}
              disabled={isReadOnly}
              hideErrorMessage
              required
            />
          )}
        </Row>

        <div className="line" />

        <span style={{ color: "var(--primary-color)" }}>
          {intl.formatMessage({ id: "kpi-info.kpi-target" })}{" "}
          {UnitOfMeasure(values.UnitOfMeasure && values.UnitOfMeasure?.value)}
        </span>

        {values?.Frequency?.Value === "custom" ? (
          <CustomKPIMeasures isReadOnly={isEditMode} />
        ) : values.Years ? (
          <KPIMeasuresField
            isChangeKpiTarget={isReadOnly}
            disabledMeasures={values?.HasNoTarget || values?.HasNoBaseline}
          />
        ) : null}
      </Space>
    </WizardStep>
  );
}

export default Controllers;
